import { ArrowRight } from "lucide-react";
import { BrutalistBadge, GlassCard } from "@/app/components/brand-scaffold";
import { Button } from "@/app/components/ui/button";
import { heroContent } from "@/design-system/registry/homepage";

/**
 * Homepage Hero — OPC narrative (2026-04-22 cleanup).
 *
 * Layout: SINGLE column, content-width capped (max-w-6xl).
 *   1. Two badges (OPC × AI AGENT / BUILT FOR ONE-PERSON SUPPLIERS)
 *   2. Headline statement — line 1 plain, line 2 in green brutalist block
 *   3. Sub-headline (left-border bar) + body card, side-by-side at lg+
 *   4. CTA stack — primary + secondary + tertiary chip
 *
 * Why no right-side "Sample Hotline" card anymore:
 *   The previous mockup card with placeholder numbers (1,284 calls / $642 / 4.82)
 *   was visually busy AND semantically empty (fabricated data). Removing it lets
 *   the headline statement do the work it was designed to do — center stage.
 *   On 1920 viewports we accept the natural side breathing room (160px each)
 *   instead of stuffing the gap with decorative noise.
 *
 * Top padding tightened — TopNav (h-24=96px) already provides visual breathing
 * room; the previous pt-16/pt-24 was creating a 192px dead zone above content.
 */
export function HomepageHero() {
  return (
    <section
      className="relative flex items-center px-4 pt-6 sm:px-6 sm:pt-8 lg:px-8 lg:pt-10 pb-12 lg:pb-16 min-h-[calc(100svh-6rem)]"
    >
      <div className="mx-auto w-full max-w-7xl 2xl:max-w-[1440px]">
        <div className="flex flex-col gap-7 sm:gap-9 lg:gap-11">
          {/* 1) Badges */}
          <div className="flex flex-wrap gap-4 relative z-20">
            {heroContent.badges.map((badge) => (
              <BrutalistBadge
                key={badge.text}
                bgClass={badge.bgClass}
                hoverBgClass={badge.hoverBgClass}
                hoverTextClass={badge.hoverTextClass}
                className="hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
              >
                {badge.text}
              </BrutalistBadge>
            ))}
          </div>

          {/* 2) Headline statement — focal block, scales up to 2xl */}
          <div className="flex flex-col items-start gap-4 sm:gap-5">
            <h1 className="max-w-[13ch] text-[clamp(3.1rem,8vw,6rem)] font-black leading-[1.02] text-black tracking-tight text-balance">
              {heroContent.statementLines[0]}
            </h1>
            <div className="inline-block max-w-full border-[6px] border-black bg-[#A3E635] px-5 py-3 sm:px-7 sm:py-4 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
              <h1 className="max-w-[18ch] text-[clamp(3.1rem,8vw,6rem)] font-black leading-[1.02] text-black tracking-tight text-balance">
                {heroContent.statementLines[1]}
              </h1>
            </div>
          </div>

          {/* 3) Sub-headline + body paragraph — side-by-side at lg+.
                Cap each column for readable line length even when container is 1440. */}
          <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr] xl:gap-8 max-w-5xl xl:max-w-none">
            <div className="border-l-8 border-black pl-5 sm:pl-6 flex items-center">
              <p className="text-xl sm:text-2xl lg:text-[26px] xl:text-[28px] font-bold text-black leading-snug">
                {heroContent.subHeadline}
              </p>
            </div>
            <GlassCard bgClass="bg-white/75" hoverClass="hover:bg-white/95" className="!p-5 sm:!p-6 flex flex-col">
              <div className="space-y-2.5 text-sm sm:text-base lg:text-[15px] font-bold text-zinc-800 leading-relaxed">
                {heroContent.bodyParagraph.map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
              </div>
            </GlassCard>
          </div>

          {/* 4) CTA stack */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 pt-1">
            <Button asChild variant="default" size="lg" className="max-w-full whitespace-normal text-center leading-tight sm:text-left">
              <a href={heroContent.primaryCTA.href} className="gap-2">
                {heroContent.primaryCTA.label}
                {heroContent.primaryCTA.showArrow && (
                  <ArrowRight className="h-5 w-5" strokeWidth={3} />
                )}
              </a>
            </Button>
            <Button asChild variant="secondary" size="lg" className="max-w-full whitespace-normal text-center leading-tight sm:text-left">
              <a href={heroContent.secondaryCTA.href}>
                {heroContent.secondaryCTA.label}
              </a>
            </Button>
            <Button asChild variant="secondary" size="lg" className="max-w-full whitespace-normal text-center leading-tight sm:text-left">
              <a href={heroContent.tertiaryLink.href} className="gap-2">
                {heroContent.tertiaryLink.label}
                <ArrowRight className="h-5 w-5" strokeWidth={3} />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
